import { DOT, EMPTY, MAX_SIZE, SHADED, type CellState } from "./types";
import type { NorinoriDefinition, NorinoriProgress, NorinoriSubmission } from "./types";

const DEFAULT_RULES = {
  en: "Shade exactly two cells in every region. Every shaded cell touches exactly one other shaded cell by a side, so the shaded cells form dominoes, which may cross region borders.",
  fr: "Noircissez exactement deux cases dans chaque région. Chaque case noircie touche exactement une autre case noircie par un côté : les cases noircies forment des dominos, qui peuvent chevaucher deux régions.",
};

export function emptyDefinition(): NorinoriDefinition {
  return {
    puzzleType: "norinori",
    typeVersion: 1,
    rows: 6,
    cols: 6,
    regions: Array.from({ length: 6 }, () => "A".repeat(6)),
    rulesText: { ...DEFAULT_RULES },
  };
}

export function clampSize(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(MAX_SIZE, Math.max(1, Math.trunc(value)));
}

export function blankRows(rows: number, cols: number, fill: CellState = EMPTY): string[] {
  return Array.from({ length: rows }, () => fill.repeat(cols));
}

/** Crops or pads a grid to the given size, keeping the top-left corner. */
export function resizeRows(source: string[], rows: number, cols: number, pad = EMPTY): string[] {
  return Array.from({ length: rows }, (_, r) => {
    const row = source[r] ?? "";
    return row.length >= cols ? row.slice(0, cols) : row + pad.repeat(cols - row.length);
  });
}

function sanitizeRow(raw: unknown, allowed: readonly string[]): string {
  if (typeof raw !== "string") return "";
  return Array.from(raw, (char) => (allowed.includes(char) ? char : EMPTY)).join("");
}

export function emptyProgress(definition: NorinoriDefinition): NorinoriProgress {
  return { version: 1, rows: blankRows(definition.rows, definition.cols) };
}

/** Accepts any stored blob (older versions, null, garbage) and returns a valid progress. */
export function migrateProgress(definition: NorinoriDefinition, raw: unknown): NorinoriProgress {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw))
    return emptyProgress(definition);
  const source = (raw as { rows?: unknown }).rows;
  if (!Array.isArray(source)) return emptyProgress(definition);
  const rows = source.map((row) => sanitizeRow(row, [SHADED, EMPTY, DOT]));
  return { version: 1, rows: resizeRows(rows, definition.rows, definition.cols) };
}

/** The server only reads shaded and unshaded: dots are the player's own notes. */
export function serializeSubmission(
  definition: NorinoriDefinition,
  progress: NorinoriProgress,
): NorinoriSubmission {
  return {
    rows: resizeRows(progress.rows, definition.rows, definition.cols).map((row) =>
      row.replaceAll(DOT, EMPTY),
    ),
  };
}
